import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ControlledSortColumnHeader } from '@/components/table/SortColumnHeader';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import { Input } from '@/components/ui/input';
import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import {
  useLinkedFilesService,
  useLinkFilesService,
  useUnlinkFilesService,
  useWorkspaceFilesService,
  useWorkspaceFileUploadService,
} from '@/services/tableViews';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  FileText,
  Loader2,
  Plus,
  Search,
  Trash,
  Upload,
} from 'lucide-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from 'react';
import { toast } from 'sonner';

interface FileRowData {
  id: string;
  name: string;
  createdAt: string;
  isLinked: boolean;
  rowId?: string;
}

interface FilesPanelProps {
  tableViewId: string;
}

export const FilesPanel: FC<FilesPanelProps> = ({ tableViewId }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(
    new Set()
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<{
    column: 'name' | 'createdAt';
    order: 'asc' | 'desc';
  } | null>(null);
  const pageSize = 50;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const shiftKeyRef = useRef(false);
  const anchorIndexRef = useRef<number | null>(null);

  const { files: workspaceFiles, isPending: isLoadingFiles } =
    useWorkspaceFilesService();
  const { linkedFiles, isPending: isLoadingLinked } =
    useLinkedFilesService(tableViewId);
  const { uploadFiles, isUploading } = useWorkspaceFileUploadService();
  const { linkFiles, isLinking } = useLinkFilesService();
  const { unlinkFiles, isUnlinking } = useUnlinkFilesService();

  const isLoading = isLoadingFiles || isLoadingLinked;
  const isProcessing = isUploading || isLinking || isUnlinking;

  // Create a set of linked file IDs for quick lookup
  const linkedFileIds = useMemo(
    () => new Set(linkedFiles.map(f => f.id)),
    [linkedFiles]
  );

  // Create a map of file ID to row ID for linked files
  const fileToRowMap = useMemo(
    () => new Map(linkedFiles.map(f => [f.id, f.rowId])),
    [linkedFiles]
  );

  // Combine workspace files with linked status
  const allFiles: FileRowData[] = useMemo(() => {
    return workspaceFiles.map(file => ({
      id: file.id,
      name: file.name,
      createdAt: file.createdAt,
      isLinked: linkedFileIds.has(file.id),
      rowId: fileToRowMap.get(file.id),
    }));
  }, [workspaceFiles, linkedFileIds, fileToRowMap]);

  // Filter files by search query
  const filteredFiles = useMemo(() => {
    if (!searchQuery.trim()) return allFiles;
    const query = searchQuery.toLowerCase();
    return allFiles.filter(file => file.name.toLowerCase().includes(query));
  }, [allFiles, searchQuery]);

  // Sort files
  const sortedFiles = useMemo(() => {
    if (!sortConfig) return filteredFiles;
    return [...filteredFiles].sort((a, b) => {
      if (sortConfig.column === 'name') {
        const cmp = a.name.localeCompare(b.name);
        return sortConfig.order === 'asc' ? cmp : -cmp;
      }
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return sortConfig.order === 'asc' ? dateA - dateB : dateB - dateA;
    });
  }, [filteredFiles, sortConfig]);

  // Pagination
  const totalPages = Math.ceil(sortedFiles.length / pageSize);
  const paginatedFiles = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedFiles.slice(start, start + pageSize);
  }, [sortedFiles, currentPage, pageSize]);

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Track shift key state for range selection
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') shiftKeyRef.current = true;
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') shiftKeyRef.current = false;
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  const selectedOnPage = paginatedFiles.filter(f =>
    selectedFileIds.has(f.id)
  ).length;
  const isAllSelected =
    paginatedFiles.length > 0 && selectedOnPage === paginatedFiles.length;
  const isIndeterminate =
    selectedOnPage > 0 && selectedOnPage < paginatedFiles.length;

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedFileIds(prev => {
        const next = new Set(prev);
        paginatedFiles.forEach(f => next.delete(f.id));
        return next;
      });
    } else {
      setSelectedFileIds(prev => {
        const next = new Set(prev);
        paginatedFiles.forEach(f => next.add(f.id));
        return next;
      });
    }
  };

  const handleSelectFile = useCallback(
    (fileId: string, index: number) => {
      if (shiftKeyRef.current && anchorIndexRef.current !== null) {
        // Shift+click: select all rows between anchor and target
        const start = Math.min(anchorIndexRef.current, index);
        const end = Math.max(anchorIndexRef.current, index);
        setSelectedFileIds(prev => {
          const next = new Set(prev);
          for (let i = start; i <= end; i++) {
            next.add(paginatedFiles[i].id);
          }
          return next;
        });
        // Anchor does NOT move on shift+click
      } else {
        // Normal or Ctrl/Cmd click: toggle item, set as new anchor
        setSelectedFileIds(prev => {
          const next = new Set(prev);
          if (next.has(fileId)) {
            next.delete(fileId);
          } else {
            next.add(fileId);
          }
          return next;
        });
        anchorIndexRef.current = index;
      }
    },
    [paginatedFiles]
  );

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;

    try {
      const result = await uploadFiles(files);
      const newIds = result.registeredFiles.map(f => f.id);
      setSelectedFileIds(prev => new Set([...prev, ...newIds]));
      toast.success(`Uploaded ${files.length} file(s)`);
    } catch {
      toast.error('Failed to upload files');
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleToggleLink = async (file: FileRowData) => {
    try {
      if (file.isLinked) {
        await unlinkFiles({
          tableViewId,
          data: { fileIds: [file.id] },
        });
        toast.success('File removed from table');
      } else {
        await linkFiles({
          tableViewId,
          data: { fileIds: [file.id], mode: 'append' },
        });
        toast.success('File added to table');
      }
    } catch {
      toast.error(
        file.isLinked ? 'Failed to remove file' : 'Failed to add file'
      );
    }
  };

  const handleAddDocuments = async () => {
    const unlinkedSelectedFiles = Array.from(selectedFileIds).filter(
      id => !linkedFileIds.has(id)
    );

    if (unlinkedSelectedFiles.length === 0) {
      toast.info('All selected files are already in the table');
      return;
    }

    try {
      const result = await linkFiles({
        tableViewId,
        data: { fileIds: unlinkedSelectedFiles, mode: 'append' },
      });
      toast.success(`Added ${result.linkedCount} document(s) to the table`);
      setSelectedFileIds(new Set());
    } catch {
      toast.error('Failed to add documents');
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
  };

  const cycleColumnSort = (column: 'name' | 'createdAt') => {
    setSortConfig(prev => {
      if (!prev || prev.column !== column) {
        return { column, order: 'asc' };
      }
      if (prev.order === 'asc') return { column, order: 'desc' };
      return null;
    });
  };

  const selectedCount = selectedFileIds.size;
  const startIdx = (currentPage - 1) * pageSize + 1;
  const endIdx = Math.min(currentPage * pageSize, sortedFiles.length);

  if (isLoading) {
    return (
      <FlexCol className="flex-1 items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
        <Text variant="body-sm" color="secondary" className="mt-2">
          Loading documents...
        </Text>
      </FlexCol>
    );
  }

  return (
    <FlexCol className="flex-1 overflow-hidden min-h-0">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFileChange}
        accept=".pdf,.doc,.docx,.txt,.md,.csv,.xlsx,.xls"
      />
      {/* Content */}
      <FlexCol gap="4" className="flex-1 overflow-hidden min-h-0 px-4 py-4">
        {/* Search and upload */}
        <FlexRow gap="4" justifyContent="between" alignItems="center">
          <div className="relative w-[320px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500" />
            <Input
              placeholder="Search documents"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleUploadClick}
            disabled={isUploading}
          >
            {isUploading ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Upload className="w-3 h-3" />
            )}
            Upload documents
          </Button>
        </FlexRow>

        {/* Table */}
        {allFiles.length === 0 ? (
          <div className="bg-white border border-neutral-200 rounded-lg py-12 px-6 flex flex-col items-center gap-3 flex-1">
            <div className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center">
              <FileText className="w-6 h-6 text-neutral-500" />
            </div>
            <div className="text-center">
              <Text variant="body" className="font-medium">
                No documents uploaded yet
              </Text>
              <Text
                variant="body-sm"
                color="secondary"
                className="max-w-[290px]"
              >
                Upload documents to add them as rows to your table view.
              </Text>
            </div>
            <Button variant="dark" size="sm" onClick={handleUploadClick}>
              <Upload className="w-3 h-3" />
              Upload documents
            </Button>
          </div>
        ) : (
          <div className="flex-1 overflow-hidden border border-neutral-200 rounded-lg bg-white flex flex-col">
            {/* Table Header */}
            <div className="flex items-center h-11 border-b border-neutral-200 shrink-0">
              <div className="w-10 flex items-center justify-center px-2">
                <Checkbox
                  checked={isIndeterminate ? 'indeterminate' : isAllSelected}
                  onCheckedChange={handleSelectAll}
                />
              </div>
              <div className="flex-1 px-2 min-w-0">
                <ControlledSortColumnHeader
                  label={
                    <Text variant="body-sm" className="font-medium">
                      Document
                    </Text>
                  }
                  sortDirection={
                    sortConfig?.column === 'name' ? sortConfig.order : false
                  }
                  onSortClick={() => cycleColumnSort('name')}
                />
              </div>
              <div className="w-[150px] px-2 min-w-0 shrink-0">
                <ControlledSortColumnHeader
                  label={
                    <Text variant="body-sm" className="font-medium">
                      Date added
                    </Text>
                  }
                  sortDirection={
                    sortConfig?.column === 'createdAt'
                      ? sortConfig.order
                      : false
                  }
                  onSortClick={() => cycleColumnSort('createdAt')}
                />
              </div>
              <div className="w-12" />
            </div>

            {/* Table Body */}
            <div className="flex-1 overflow-y-auto">
              {paginatedFiles.map((file, index) => (
                <div
                  key={file.id}
                  className={cn(
                    'flex items-center h-11 border-b border-neutral-200',
                    selectedFileIds.has(file.id)
                      ? 'bg-neutral-100'
                      : 'bg-white hover:bg-neutral-50'
                  )}
                >
                  <div className="w-10 flex items-center justify-center px-2">
                    <Checkbox
                      checked={selectedFileIds.has(file.id)}
                      onCheckedChange={() => handleSelectFile(file.id, index)}
                    />
                  </div>
                  <div className="flex-1 flex items-center gap-2 px-2 min-w-0">
                    <FileText className="w-4 h-4 text-neutral-700 shrink-0" />
                    <Text
                      variant="body"
                      className="font-medium truncate text-neutral-700"
                    >
                      {file.name}
                    </Text>
                  </div>
                  <div className="w-[100px] px-2">
                    <Text variant="body-sm" color="primary">
                      {formatDate(file.createdAt)}
                    </Text>
                  </div>
                  <div className="w-12 flex items-center justify-center">
                    <Button
                      variant={file.isLinked ? 'ghost' : 'outline'}
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleToggleLink(file)}
                      disabled={isProcessing}
                    >
                      {file.isLinked ? (
                        <Trash className="w-4 h-4 text-red-600" />
                      ) : (
                        <Plus className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        {allFiles.length > 0 && (
          <FlexRow
            justifyContent="between"
            alignItems="center"
            className="px-4 shrink-0"
          >
            <Text variant="body-sm" color="secondary">
              {selectedCount > 0
                ? `${selectedCount} document${selectedCount > 1 ? 's' : ''} selected`
                : '\u00A0'}
            </Text>
            <FlexRow gap="4" alignItems="center">
              {/* Pagination */}
              <FlexRow gap="4" alignItems="center">
                <Text variant="body-sm" className="font-medium">
                  {startIdx}-{endIdx} of {sortedFiles.length}
                </Text>
                <FlexRow gap="2">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(1)}
                  >
                    <ChevronsLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    disabled={currentPage === totalPages}
                    onClick={() =>
                      setCurrentPage(p => Math.min(totalPages, p + 1))
                    }
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(totalPages)}
                  >
                    <ChevronsRight className="w-4 h-4" />
                  </Button>
                </FlexRow>
              </FlexRow>
            </FlexRow>
          </FlexRow>
        )}
      </FlexCol>

      {/* CTA */}
      <div className="border-t border-neutral-200 p-4 flex justify-end shrink-0">
        <Button
          variant="dark"
          onClick={handleAddDocuments}
          disabled={selectedCount === 0 || isProcessing}
        >
          {isLinking ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Adding...
            </>
          ) : (
            <>
              <Plus className="w-4 h-4" />
              Add documents
            </>
          )}
        </Button>
      </div>
    </FlexCol>
  );
};
